import { symphonyAbi, symphonyAddress } from "../../common/constants/contract";
import { Contract, Interface, isAddress } from "ethers-v6";
import { getTotalAmountIn } from "./getTotalAmountIn";
import { getConfig } from "../constants/config";
import { calculateAmountOutMin } from "./calculateAmountOutMin";
import { getTokenIn } from "./getTokenIn";
import { getTokenOut } from "./getTokenOut";

/**
 * Encodes the calldata for a swap transaction on the Symphony protocol without executing it.
 * This is useful for transaction simulation using eth_call.
 *
 * @param {Object} params - Parameters for encoding swap calldata
 * @param {Array} params.route - Route information from getRoute
 * @param {boolean} params.includesNative - Whether the swap includes native token
 * @param {Object} [params.slippage] - Slippage configuration
 * @param {number} [params.slippage.slippageAmount=config.slippage] - Slippage amount
 * @param {boolean} [params.slippage.isRaw=true] - If true, slippage is in raw units
 * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
 * @param {number} [params.slippage.outTokenDecimals] - Output token decimals
 * @returns {Object} Object containing the encoded calldata and transaction value (if needed)
 *
 * @example
 * const route = await getRoute({
 *   tokenIn: "0x0", // SEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const { calldata, value } = generateCalldata({
 *   route,
 *   includesNative: true,
 *   slippage: {
 *     slippageAmount: 50,
 *     isRaw: false,
 *     isBps: true
 *   }
 * });
 */
export const generateCalldata = async ({
  from,
  route,
  includesNative,
  options = {},
  slippage = {
    slippageAmount: getConfig().slippage,
    isBps: false,
  },
}) => {
  try {
    let fromAddress;
    if (typeof from === "string" && isAddress(from)) {
      // Check if the string is a valid Ethereum address
      fromAddress = from;
    } else if (from && typeof from.getAddress === "function") {
      // If it has getAddress method, use it
      fromAddress = await from.getAddress();
    } else {
      throw new Error(
        "Invalid 'from' parameter: must be a valid Ethereum address or a signer with getAddress() method"
      );
    }

    const { nativeAddress, wrappedNativeAddress, functionName, feeParams } =
      getConfig();
    let tokenIn = getTokenIn(route);
    let tokenOut = getTokenOut(route);

    // If native currency included, treat wrapped currency as native
    includesNative
      ? tokenIn === wrappedNativeAddress
        ? (tokenIn = nativeAddress)
        : (tokenOut = nativeAddress)
      : null;

    // Calculate minimum output amount based on slippage
    const amountOutMin = await calculateAmountOutMin({
      variable: route,
      slippageAmount: slippage.slippageAmount,
      options: {
        isBps: slippage.isBps ?? false,
      },
    });

    // Create contract interface for encoding
    const iface = new Interface(symphonyAbi);
    let calldata;
    let value = "0";

    // NATIVE TO TOKEN TRANSACTION
    if (tokenIn === nativeAddress && tokenOut !== nativeAddress) {
      calldata = iface.encodeFunctionData(functionName, [
        route,
        amountOutMin,
        false,
        [
          feeParams.paramFee,
          feeParams.feeAddress,
          feeParams.feeSharePercentage,
        ],
      ]);
      value = (await getTotalAmountIn(route)).amountIn.toString();
    }
    // TOKEN TO NATIVE TRANSACTION
    else if (tokenIn !== wrappedNativeAddress && tokenOut === nativeAddress) {
      calldata = iface.encodeFunctionData(functionName, [
        route,
        amountOutMin,
        true,
        [
          feeParams.paramFee,
          feeParams.feeAddress,
          feeParams.feeSharePercentage,
        ],
      ]);
    }
    // TOKEN TO TOKEN TRANSACTION
    else if (tokenIn !== nativeAddress && tokenOut !== nativeAddress) {
      calldata = iface.encodeFunctionData(functionName, [
        route,
        amountOutMin,
        false,
        [
          feeParams.paramFee,
          feeParams.feeAddress,
          feeParams.feeSharePercentage,
        ],
      ]);
    } else {
      throw new Error("Invalid token combination for swap");
    }

    return {
      from: fromAddress,
      to: symphonyAddress,
      data: calldata,
      value,
    };
  } catch (error) {
    console.error("Failed to encode swap calldata:", error);
    throw error;
  }
};
